// The Company tab's "Q4 research" section: the strategist's hand research for a Q4 October
// target -- the context fields, the why-now, the cited evidence with links and dates, and the
// gaps. Empty fields are hidden; nothing renders for an account on no Q4 list.
import type { BookAccount, Q4WhyNow } from '../api/types';
import { q4ContextRows, q4DayLabel, q4EvidenceItems, q4Heading } from '../lib/q4Target';
import { safeHref, sourceHost } from './overviewSource';
import { PanelSection } from './panelParts';

/** A source link through `safeHref`: an http(s) URL is a link, anything else nothing. */
function SourceLink({ url }: { url: string | null }) {
  const href = safeHref(url);
  if (!href) return null;
  return <a href={href} target="_blank" rel="noopener noreferrer">{sourceHost(href)} ↗</a>;
}

/** The why-now line, dated and linked, marked when it is Exa news nobody reviewed. */
function WhyNowLine({ whyNow }: { whyNow: Q4WhyNow }) {
  const day = q4DayLabel(whyNow.on);
  return (
    <div className="factline">
      <dt>Why now</dt>
      <dd>
        {whyNow.text}
        {day ? <span className="muted"> · {day}</span> : null}
        {whyNow.unreviewed ? <span className="q4unreviewed"> unreviewed Exa news</span> : null}
        {' '}<SourceLink url={whyNow.url} />
      </dd>
    </div>
  );
}

export function Q4Research({ row }: { row: BookAccount }) {
  const target = row.q4_target;
  if (!target) return null;
  const contextRows = q4ContextRows(target);
  const evidence = q4EvidenceItems(target);
  const gaps = target.research_gaps?.trim();
  const depth = target.research_depth?.trim();

  return (
    <PanelSection title={q4Heading(target)} aside={depth ? `depth: ${depth}` : null}>
      {/* The context fields that hold something, then the why-now. */}
      {contextRows.length || target.why_now ? (
        <dl className="factlines q4facts">
          {contextRows.map(entry => (
            <div key={entry.label} className="factline"><dt>{entry.label}</dt><dd>{entry.text}</dd></div>
          ))}
          {target.why_now?.text ? <WhyNowLine whyNow={target.why_now} /> : null}
        </dl>
      ) : null}

      {/* Each cited fact with its kind, its date and its source. */}
      {evidence.length ? (
        <ul className="newslist q4evidence">
          {evidence.map((item, index) => (
            <li key={`evidence-${index}`}>
              <span className="newsday">{item.day}</span>
              <span className="newstext"><span className="q4evidencekind">{item.label}</span> {item.text}</span>
              {item.href ? <a href={item.href} target="_blank" rel="noopener noreferrer">{sourceHost(item.href)} ↗</a> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {gaps ? <p className="q4gaps"><b>Research gaps:</b> {gaps}</p> : null}
    </PanelSection>
  );
}
